// ABOUTME: Presentational form for the Delivery Rate (load spacing) entity.
// ABOUTME: Renders timeOnJobSite, spacing, flowRate, and stockpiling sections based on scenario flags.
import {TextMedium} from '@hm/fundamental'
import React, {useEffect, useMemo} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {LoadSpacingConfig, LoadSpacingFormValues, LoadSpacingParameterFormValues} from '../types/loadSpacing'
import {CountryConfigsSwitchControl} from './CountryConfigsSwitchControl'
import {LoadSpacingFieldGroupSection} from './LoadSpacingFieldGroupSection'

export interface DeliveryRateFormProps {
  config: LoadSpacingConfig
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: LoadSpacingFormValues) => void
  timeOnJobSiteUomOptions: {value: string; label: string; symbol?: string}[]
  spacingUomOptions: {value: string; label: string; symbol?: string}[]
  flowRateUomOptions: {value: string; label: string; symbol?: string}[]
  showTimeOnJobSite: boolean
  showSpacing: boolean
  showFlowRate: boolean
  showStockpiling: boolean
  /** Country-default (root) scope: every enabled field must be explicitly overridden. */
  isRoot?: boolean
}

const toFormValues = (cfg: LoadSpacingConfig): LoadSpacingFormValues => ({
  allowStockpiling: cfg.allowStockpiling,
  timeOnJobSite: cfg.timeOnJobSite,
  spacing: cfg.spacing,
  flowRate: cfg.flowRate,
})

export const DeliveryRateForm: React.FC<DeliveryRateFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  timeOnJobSiteUomOptions,
  spacingUomOptions,
  flowRateUomOptions,
  showTimeOnJobSite,
  showSpacing,
  showFlowRate,
  showStockpiling,
  isRoot = false,
}) => {
  const {t} = useTranslation()

  const {control, reset, setValue, formState: {isDirty}} = useForm<LoadSpacingFormValues>({
    defaultValues: useMemo(() => toFormValues(config), [config]),
  })

  useEffect(() => {
    reset(toFormValues(config))
  }, [config, reset])

  const allowStockpiling = useWatch({control, name: 'allowStockpiling'})
  const timeOnJobSite = useWatch({control, name: 'timeOnJobSite'})
  const spacing = useWatch({control, name: 'spacing'})
  const flowRate = useWatch({control, name: 'flowRate'})

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const paramValidity = useMemo(() => {
    const getNum = (ov: {isOverridden: boolean; value: number | null} | null | undefined): number | null =>
      ov?.isOverridden ? (ov.value ?? null) : null

    const isNonNeg = (n: number | null) => n === null || n >= 0

    const validateParam = (p: LoadSpacingParameterFormValues | null): boolean => {
      if (!p) return true
      const uomOk = isRoot
        ? Boolean(p.uomId?.isOverridden && p.uomId.value)
        : !p.uomId?.isOverridden || Boolean(p.uomId.value)
      // At root (country default) there is no parent to inherit from: every field must be overridden.
      const requiredOk = !isRoot || [p.default, p.min, p.max, p.step].every((f) => f?.isOverridden === true)
      const min = getNum(p.min)
      const max = getNum(p.max)
      const def = getNum(p.default)
      const step = getNum(p.step)
      const rangeOk = min === null || max === null || min <= max
      const defOk = def === null || ((min === null || def >= min) && (max === null || def <= max))
      const nonNegOk = [min, max, def].every(isNonNeg)
      const stepOk = step === null || step > 0
      return uomOk && requiredOk && rangeOk && defOk && nonNegOk && stepOk
    }

    return {
      timeOnJobSite: validateParam(showTimeOnJobSite ? timeOnJobSite : null),
      spacing: validateParam(showSpacing ? spacing : null),
      flowRate: validateParam(showFlowRate ? flowRate : null),
    }
  }, [showTimeOnJobSite, showSpacing, showFlowRate, timeOnJobSite, spacing, flowRate, isRoot])

  const paramErrors = useMemo(() => {
    const req = t('countryConfigurations.loadSpacing.errors.requiredAtCountryLevel')
    const getErrors = (p: LoadSpacingParameterFormValues | null) => {
      if (!p) return {}
      const min = p.min?.isOverridden ? (p.min.value ?? null) : null
      const max = p.max?.isOverridden ? (p.max.value ?? null) : null
      const def = p.default?.isOverridden ? (p.default.value ?? null) : null
      const step = p.step?.isOverridden ? (p.step.value ?? null) : null
      const minInvalid = min !== null && min < 0
      const maxInvalid = max !== null && max < 0
      const minGtMax = !minInvalid && !maxInvalid && min !== null && max !== null && min > max
      const defNeg = def !== null && def < 0
      const defOutOfRange = !defNeg && def !== null && (
        (min !== null && def < min) || (max !== null && def > max)
      )
      return {
        uom: isRoot && (!p.uomId?.isOverridden || !p.uomId.value)
          ? req
          : p.uomId?.isOverridden && !p.uomId.value
            ? t('countryConfigurations.loadSpacing.errors.uomRequired')
            : undefined,
        default: isRoot && !p.default?.isOverridden
          ? req
          : defNeg
            ? t('countryConfigurations.loadSpacing.errors.valueMustBeNonNegative')
            : defOutOfRange
              ? t('countryConfigurations.loadSpacing.errors.defaultOutOfRange')
              : undefined,
        min: isRoot && !p.min?.isOverridden
          ? req
          : minInvalid
            ? t('countryConfigurations.loadSpacing.errors.valueMustBeNonNegative')
            : minGtMax
              ? t('countryConfigurations.loadSpacing.errors.minGreaterThanMax')
              : undefined,
        max: isRoot && !p.max?.isOverridden
          ? req
          : maxInvalid
            ? t('countryConfigurations.loadSpacing.errors.valueMustBeNonNegative')
            : minGtMax
              ? t('countryConfigurations.loadSpacing.errors.maxLessThanMin')
              : undefined,
        step: isRoot && !p.step?.isOverridden
          ? req
          : step !== null && step <= 0
            ? t('countryConfigurations.loadSpacing.errors.stepMustBePositive')
            : undefined,
      }
    }
    return {
      timeOnJobSite: getErrors(showTimeOnJobSite ? timeOnJobSite : null),
      spacing: getErrors(showSpacing ? spacing : null),
      flowRate: getErrors(showFlowRate ? flowRate : null),
    }
  }, [showTimeOnJobSite, showSpacing, showFlowRate, timeOnJobSite, spacing, flowRate, isRoot, t])

  const stockpilingValid = !isRoot || !showStockpiling || allowStockpiling?.isOverridden === true
  const isValid =
    paramValidity.timeOnJobSite && paramValidity.spacing && paramValidity.flowRate && stockpilingValid

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    onValuesChange?.({allowStockpiling, timeOnJobSite, spacing, flowRate})
  }, [allowStockpiling, timeOnJobSite, spacing, flowRate, onValuesChange])

  return (
    <div className="flex flex-col gap-6 p-6 w-full" data-cy="delivery-rate-form">
      {showTimeOnJobSite && (
        <>
          <LoadSpacingFieldGroupSection
            title={t('countryConfigurations.loadSpacing.timeOnJobSite.title')}
            titleTooltip={t('countryConfigurations.loadSpacing.timeOnJobSite.tooltip')}
            value={timeOnJobSite}
            onChange={(next) => setValue('timeOnJobSite', next, {shouldDirty: true})}
            uomOptions={timeOnJobSiteUomOptions}
            isReadOnly={isReadOnly}
            defaultTooltip={t('countryConfigurations.loadSpacing.defaultTooltip')}
            minimumTooltip={t('countryConfigurations.loadSpacing.minimumTooltip')}
            maximumTooltip={t('countryConfigurations.loadSpacing.maximumTooltip')}
            stepTooltip={t('countryConfigurations.loadSpacing.stepTooltip')}
            errors={paramErrors.timeOnJobSite}
          />
          {(showSpacing || showFlowRate || showStockpiling) && (
            <hr className="border-border w-full" />
          )}
        </>
      )}
      {showSpacing && (
        <>
          <LoadSpacingFieldGroupSection
            title={t('countryConfigurations.loadSpacing.spacing.title')}
            titleTooltip={t('countryConfigurations.loadSpacing.spacing.tooltip')}
            value={spacing}
            onChange={(next) => setValue('spacing', next, {shouldDirty: true})}
            uomOptions={spacingUomOptions}
            isReadOnly={isReadOnly}
            defaultTooltip={t('countryConfigurations.loadSpacing.defaultTooltip')}
            minimumTooltip={t('countryConfigurations.loadSpacing.minimumTooltip')}
            maximumTooltip={t('countryConfigurations.loadSpacing.maximumTooltip')}
            stepTooltip={t('countryConfigurations.loadSpacing.stepTooltip')}
            errors={paramErrors.spacing}
          />
          {(showFlowRate || showStockpiling) && <hr className="border-border w-full" />}
        </>
      )}
      {showFlowRate && (
        <>
          <LoadSpacingFieldGroupSection
            title={t('countryConfigurations.loadSpacing.flowRate.title')}
            titleTooltip={t('countryConfigurations.loadSpacing.flowRate.tooltip')}
            value={flowRate}
            onChange={(next) => setValue('flowRate', next, {shouldDirty: true})}
            uomOptions={flowRateUomOptions}
            isReadOnly={isReadOnly}
            defaultTooltip={t('countryConfigurations.loadSpacing.defaultTooltip')}
            minimumTooltip={t('countryConfigurations.loadSpacing.minimumTooltip')}
            maximumTooltip={t('countryConfigurations.loadSpacing.maximumTooltip')}
            stepTooltip={t('countryConfigurations.loadSpacing.stepTooltip')}
            errors={paramErrors.flowRate}
          />
          {showStockpiling && <hr className="border-border w-full" />}
        </>
      )}
      {showStockpiling && (
        <div className="flex flex-col gap-6">
          <span className="text-lg font-semibold leading-6 text-foreground">
            {t('countryConfigurations.loadSpacing.stockpiling.title')}
          </span>
          <div className="flex gap-14 items-start w-full">
            <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
              <TextMedium className="text-foreground line-clamp-1">
                {t('countryConfigurations.loadSpacing.stockpiling.defaultValue')}
              </TextMedium>
            </div>
            <div className="w-[160px] shrink-0">
              <CountryConfigsSwitchControl
                value={allowStockpiling?.value === true ? 'yes' : 'no'}
                options={[
                  {value: 'yes', label: t('countryConfigurations.loadSpacing.stockpiling.yes')},
                  {value: 'no', label: t('countryConfigurations.loadSpacing.stockpiling.no')},
                ]}
                onChange={(v) =>
                  setValue('allowStockpiling', {isOverridden: true, value: v === 'yes'}, {shouldDirty: true})
                }
                disabled={isReadOnly}
                aria-label={t('countryConfigurations.loadSpacing.stockpiling.defaultValue')}
              />
              {!stockpilingValid && (
                <TextMedium className="text-error mt-1">
                  {t('countryConfigurations.loadSpacing.errors.requiredAtCountryLevel')}
                </TextMedium>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
